import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import api from '../../services/api';
import { AdminOrderList } from '../../types/order';
import { format } from 'date-fns';
import StaffNewOrderDialog from '../../components/StaffNewOrderDialog';

function formatMoney(price: unknown): string {
  const n = typeof price === 'number' && Number.isFinite(price) ? price : Number(price ?? 0);
  const safe = Number.isFinite(n) ? n : 0;
  return `$${safe.toFixed(2)}`;
}

function formatCellDateTime(value: unknown): string {
  if (value == null || value === '') return '—';
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) return '—';
  return format(d, 'dd MMM yyyy, h:mm a');
}

function formatCellDate(value: unknown): string {
  if (value == null || value === '') return '—';
  const d = new Date(`${String(value)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  return format(d, 'dd MMM yyyy');
}

function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const openedOrderFromUrlRef = useRef<string | null>(null);

  const [orders, setOrders] = useState<AdminOrderList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  const location = useLocation();
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [prefillServiceAddress, setPrefillServiceAddress] = useState('');

  useEffect(() => {
    const state = location.state as { autoOpenNewOrder?: boolean; prefillServiceAddress?: string } | null;
    if (state?.autoOpenNewOrder) {
      setPrefillServiceAddress(state.prefillServiceAddress ?? '');
      setNewOrderOpen(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);
  
  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const statusParams =
        statusFilter === 'active'
          ? { exclude_statuses: 'completed,cancelled,draft' }
          : statusFilter
          ? { status: statusFilter }
          : {};

      const data = await api.adminListOrders({
        page: page + 1,
        per_page: perPage,
        ...statusParams,
        ...(searchFilter ? { search: searchFilter } : {}),
        ...(unassignedOnly ? { unassigned_only: true } : {}),
      });
      setOrders(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, statusFilter, searchFilter, unassignedOnly]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);
  
  const handleViewOrder = useCallback((orderId: string) => {
    navigate(`/admin/orders/${orderId}`);
  }, [navigate]);

  useEffect(() => {
    const id = searchParams.get('openOrder');
    if (!id) {
      openedOrderFromUrlRef.current = null;
      return;
    }
    if (openedOrderFromUrlRef.current === id) return;
    openedOrderFromUrlRef.current = id;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('openOrder');
        return next;
      },
      { replace: true },
    );
    navigate(`/admin/orders/${id}`);
  }, [searchParams, navigate, setSearchParams]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'pending': return 'warning';
      case 'awaiting_approval': return 'warning';
      case 'approved': return 'success';
      case 'processing': return 'info';
      case 'provisioning': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'awaiting_approval': return 'Awaiting Approval';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };
  
  const columns: GridColDef[] = [
    { field: 'order_reference', headerName: 'Order Ref', width: 150 },
    {
      field: 'account_name',
      headerName: 'Account',
      width: 180,
      renderCell: (params: any) => params.row.account_name || '—',
    },
    { field: 'status', headerName: 'Status', width: 120, renderCell: (params: any) => (
      <Chip label={getStatusLabel(params.value)} color={getStatusColor(params.value) as any} size="small" />
    )},
    { field: 'customer_name', headerName: 'Customer', width: 200, valueGetter: (params: any) => 
      `${params.row.first_name || ''} ${params.row.last_name || ''}`.trim()
    },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'total_monthly', headerName: 'Monthly', width: 100, renderCell: (params: any) => formatMoney(params.value) },
    { field: 'total_one_time', headerName: 'One-Time', width: 100, renderCell: (params: any) => formatMoney(params.value) },
    { field: 'created_at', headerName: 'Created', width: 180, renderCell: (params: any) => formatCellDateTime(params.value) },
    { field: 'requested_activation_date', headerName: 'Activation', width: 140, renderCell: (params: any) => formatCellDate(params.value) },
    { field: 'actions', headerName: 'Actions', width: 120, sortable: false, renderCell: (params: any) => (
      <IconButton size="small" onClick={() => handleViewOrder(params.row.id)}>
        <VisibilityIcon />
      </IconButton>
    )},
  ];
  
  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5">Order Management</Typography>
          <Button variant="contained" onClick={() => setNewOrderOpen(true)}>
            New order
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Search"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="active">All — except Completed &amp; Cancelled</MenuItem>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="awaiting_approval">Awaiting Approval</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="provisioning">Provisioning</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={unassignedOnly}
                onChange={(e) => setUnassignedOnly(e.target.checked)}
                size="small"
              />
            }
            label="Unassigned only"
          />
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <DataGrid
              rows={orders}
              columns={columns}
              paginationModel={{ page, pageSize: perPage }}
              onPaginationModelChange={(model) => {
                setPage(model.page);
                setPerPage(model.pageSize);
              }}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              autoHeight
              getRowId={(row) => row.id}
            />
          </>
        )}
      </Paper>
      
      <StaffNewOrderDialog
        open={newOrderOpen}
        onClose={() => { setNewOrderOpen(false); setPrefillServiceAddress(''); }}
        profileServiceAddress={prefillServiceAddress}
        onSuccess={() => {
          loadOrders();
        }}
      />
    </Box>
  );
}

export default OrdersPage;

